// assets/product-bundle-builder.js - Fixed Version with Auto-Close and Metafield Support

// Prevent multiple initializations
if (!window.bundleBuilderInitialized) {
  window.bundleBuilderInitialized = true;

  class ProductBundleBuilder extends HTMLElement {
    constructor() {
      super();
      
      // Check if already initialized for this instance
      if (this.initialized) return;
      this.initialized = true;
      
      this.selectedProducts = new Map();
      this.currency = this.dataset.currency || '$';
      this.bundleDiscount = parseFloat(this.dataset.bundleDiscount) || 0;
      this.blockId = this.dataset.blockId;
      this.productId = this.dataset.productId;
      
      // Bind context for methods
      this.removeSelection = this.removeSelection.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      // Only initialize when element is connected to DOM
      if (!this.hasAttribute('data-initialized')) {
        this.setAttribute('data-initialized', 'true');
        this.init();
      }
    }

    disconnectedCallback() {
      // Clean up event listeners when removed from DOM
      this.cleanup();
    }

    init() {
      // Prevent double initialization
      if (this._isInitialized) return;
      this._isInitialized = true;
      
      console.log(`Initializing bundle builder: Block ${this.blockId}, Product ${this.productId}`);
      
      this.setupSelectors();
      this.setupEventListeners();
      
      // Use setTimeout to ensure DOM is ready and event listeners are attached
      setTimeout(() => {
        this.preselectCurrentProduct();
      }, 100);
      
      this.updateSummary();
    }

    cleanup() {
      // Remove global event listeners
      if (this._documentClickHandler) {
        document.removeEventListener('click', this._documentClickHandler);
      }
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
    }

    setupSelectors() {
      this.selectors = this.querySelectorAll('select-dropdown');
      this.summary = this.querySelector('[data-bundle-summary]');
      this.addToCartBtn = this.querySelector('[data-add-bundle]');
      this.errorContainer = this.querySelector('[data-bundle-error]');
    }

    setupEventListeners() {
      // Handle dropdown toggles
      this.selectors.forEach(selector => {
        const trigger = selector.querySelector('.bundle-selector-trigger');
        const dropdown = selector.querySelector('.bundle-selector-dropdown');
        
        // Remove any existing listeners first
        const newTrigger = trigger.cloneNode(true);
        trigger.parentNode.replaceChild(newTrigger, trigger);
        
        newTrigger.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleDropdown(selector);
        });

        // Handle option selections
        const inputs = selector.querySelectorAll('input');
        inputs.forEach(input => {
          // Use unique event handler per instance
          input.addEventListener('change', (e) => {
            this.handleSelection(e, selector);
          });
        });
      });

      // Close dropdowns when clicking outside - store reference for cleanup
      this._documentClickHandler = (e) => {
        if (!this.contains(e.target)) {
          this.closeAllDropdowns();
        }
      };
      document.addEventListener('click', this._documentClickHandler);

      // Handle scroll and resize events
      window.addEventListener('scroll', this.handleScroll, true);
      window.addEventListener('resize', this.handleResize);

      // Add to cart
      if (this.addToCartBtn) {
        // Remove any existing listeners
        const newBtn = this.addToCartBtn.cloneNode(true);
        this.addToCartBtn.parentNode.replaceChild(newBtn, this.addToCartBtn);
        this.addToCartBtn = newBtn;
        
        this.addToCartBtn.addEventListener('click', () => {
          this.addBundleToCart();
        });
      }

      // Make removeSelection available globally for this instance
      window[`bundleBuilder_${this.blockId}_removeSelection`] = this.removeSelection;
    }

    preselectCurrentProduct() {
      console.log('Attempting to preselect current product...');
      
      // Find the main product selector (step 1)
      const mainProductSelector = this.querySelector('[data-step="1"]');
      if (!mainProductSelector) {
        console.log('Main product selector not found');
        return;
      }

      // Try multiple strategies to find the current product
      let defaultInput = mainProductSelector.querySelector('input[data-default="true"]');
      
      // If no default is marked, try to find by product ID
      if (!defaultInput && this.productId) {
        defaultInput = mainProductSelector.querySelector(`input[data-product-id="${this.productId}"]`);
        console.log(`Looking for product with ID ${this.productId}:`, defaultInput ? 'found' : 'not found');
      }
      
      // If still not found, select the first available option
      if (!defaultInput) {
        defaultInput = mainProductSelector.querySelector('input[type="radio"]');
        console.log('Selecting first available option:', defaultInput ? 'found' : 'not found');
      }
      
      if (defaultInput) {
        // Check the input
        defaultInput.checked = true;
        
        // Get the item data including metafields
        const itemData = {
          variantId: defaultInput.value,
          productId: defaultInput.dataset.productId,
          title: defaultInput.dataset.title,
          price: parseFloat(defaultInput.dataset.price),
          comparePrice: parseFloat(defaultInput.dataset.comparePrice) || null,
          image: defaultInput.dataset.image,
          metafield1: defaultInput.dataset.metafield1 || null,
          metafield2: defaultInput.dataset.metafield2 || null
        };
        
        // Update the selection
        this.updateStepSelection('1', [itemData]);
        this.updateSelectedItemsDisplay('1');
        this.updateSummary();
        this.validateBundle();
        
        console.log('Pre-selected product:', itemData.title);
      } else {
        console.log('No product found to preselect');
      }
    }

    toggleDropdown(selector) {
      const trigger = selector.querySelector('.bundle-selector-trigger');
      const dropdown = selector.querySelector('.bundle-selector-dropdown');
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';

      if (isOpen) {
        trigger.setAttribute('aria-expanded', 'false');
        dropdown.hidden = true;
        // Clean up any appended dropdowns
        if (dropdown.dataset.appended === 'true') {
          selector.appendChild(dropdown);
          dropdown.dataset.appended = 'false';
          dropdown.style.position = '';
          dropdown.style.top = '';
          dropdown.style.left = '';
          dropdown.style.width = '';
        }
      } else {
        this.closeAllDropdowns();
        trigger.setAttribute('aria-expanded', 'true');
        dropdown.hidden = false;
        
        // Check if dropdown is cut off and needs repositioning
        this.positionDropdown(selector, dropdown);
      }
    }

    positionDropdown(selector, dropdown) {
      // Get the trigger button position
      const trigger = selector.querySelector('.bundle-selector-trigger');
      const triggerRect = trigger.getBoundingClientRect();
      
      // Check if we're in a modal or constrained container
      const modal = this.closest('.modal-content, .popup-content, [role="dialog"], .modal__inner, .popup__inner');
      
      if (modal) {
        // Append dropdown to body to escape modal constraints
        document.body.appendChild(dropdown);
        dropdown.dataset.appended = 'true';
        
        // Position the dropdown
        dropdown.style.position = 'fixed';
        dropdown.style.top = `${triggerRect.bottom + 8}px`;
        dropdown.style.left = `${triggerRect.left}px`;
        dropdown.style.width = `${triggerRect.width}px`;
        dropdown.style.maxWidth = `${Math.min(500, window.innerWidth - triggerRect.left - 20)}px`;
        
        // Check if dropdown goes off bottom of screen
        const dropdownRect = dropdown.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        
        if (dropdownRect.bottom > viewportHeight) {
          // Position above the trigger instead
          dropdown.style.top = `${triggerRect.top - dropdownRect.height - 8}px`;
        }
      }
    }

    handleScroll() {
      // Reposition any open dropdowns on scroll
      const openDropdown = this.querySelector('.bundle-selector-dropdown:not([hidden])');
      if (openDropdown && openDropdown.dataset.appended === 'true') {
        const selector = openDropdown.closest('select-dropdown') || this.querySelector(`select-dropdown:has(.bundle-selector-dropdown[data-appended="true"])`);
        if (selector) {
          this.positionDropdown(selector, openDropdown);
        }
      }
    }

    handleResize() {
      // Close dropdowns on resize to prevent positioning issues
      this.closeAllDropdowns();
    }

    closeAllDropdowns() {
      this.querySelectorAll('.bundle-selector-trigger').forEach(trigger => {
        trigger.setAttribute('aria-expanded', 'false');
        const dropdown = trigger.nextElementSibling;
        if (dropdown) {
          dropdown.hidden = true;
          // Clean up any appended dropdowns
          if (dropdown.dataset.appended === 'true') {
            const selector = dropdown.closest('select-dropdown') || this.querySelector(`select-dropdown:has(.bundle-selector-dropdown[data-appended="true"])`);
            if (selector) {
              selector.appendChild(dropdown);
            }
            dropdown.dataset.appended = 'false';
            dropdown.style.position = '';
            dropdown.style.top = '';
            dropdown.style.left = '';
            dropdown.style.width = '';
          }
        }
      });
    }

    closeDropdown(selector) {
      const trigger = selector.querySelector('.bundle-selector-trigger');
      const dropdown = selector.querySelector('.bundle-selector-dropdown');
      
      if (trigger && dropdown) {
        trigger.setAttribute('aria-expanded', 'false');
        dropdown.hidden = true;
        
        // Clean up any appended dropdowns
        if (dropdown.dataset.appended === 'true') {
          selector.appendChild(dropdown);
          dropdown.dataset.appended = 'false';
          dropdown.style.position = '';
          dropdown.style.top = '';
          dropdown.style.left = '';
          dropdown.style.width = '';
        }
      }
    }

    handleSelection(event, selector) {
      const input = event.target;
      const step = selector.dataset.step;
      const maxSelections = parseInt(selector.dataset.maxSelections);
      const selectedItemsContainer = this.querySelector(`[data-selected-items="${step}"]`);

      // Get data from input including metafields
      const itemData = {
        variantId: input.value,
        productId: input.dataset.productId,
        title: input.dataset.title,
        price: parseFloat(input.dataset.price),
        comparePrice: parseFloat(input.dataset.comparePrice) || null,
        image: input.dataset.image,
        // Add metafield data
        metafield1: input.dataset.metafield1 || null,
        metafield2: input.dataset.metafield2 || null
      };

      // Handle radio buttons (max 1 selection)
      if (input.type === 'radio') {
        this.updateStepSelection(step, [itemData]);
        // Close dropdown for radio buttons
        this.closeDropdown(selector);
      } else {
        // Handle checkboxes (multiple selections)
        const checkedInputs = selector.querySelectorAll('input:checked');
        
        if (checkedInputs.length > maxSelections) {
          input.checked = false;
          this.showError(`Maximum ${maxSelections} selections allowed for this step`);
          return;
        }

        const selections = Array.from(checkedInputs).map(checkedInput => ({
          variantId: checkedInput.value,
          productId: checkedInput.dataset.productId,
          title: checkedInput.dataset.title,
          price: parseFloat(checkedInput.dataset.price),
          comparePrice: parseFloat(checkedInput.dataset.comparePrice) || null,
          image: checkedInput.dataset.image,
          metafield1: checkedInput.dataset.metafield1 || null,
          metafield2: checkedInput.dataset.metafield2 || null
        }));

        this.updateStepSelection(step, selections);
        
        // Close dropdown when max selections reached or optionally after each selection
        if (checkedInputs.length >= maxSelections) {
          this.closeDropdown(selector);
        }
        // Uncomment the line below to close dropdown after each checkbox selection
        // this.closeDropdown(selector);
      }

      this.updateSelectedItemsDisplay(step);
      this.updateSummary();
      this.validateBundle();
    }

    updateStepSelection(step, selections) {
      if (selections.length === 0) {
        this.selectedProducts.delete(step);
      } else {
        this.selectedProducts.set(step, selections);
      }
    }

    updateSelectedItemsDisplay(step) {
      const container = this.querySelector(`[data-selected-items="${step}"]`);
      const selections = this.selectedProducts.get(step) || [];
      
      // Get metafield labels from block settings
      const blockElement = this.closest('[data-block-id]');
      const metafield1Label = blockElement?.dataset.metafield1Label || this.dataset.metafield1Label;
      const metafield2Label = blockElement?.dataset.metafield2Label || this.dataset.metafield2Label;
      
      container.innerHTML = selections.map(item => {
        let metafieldHtml = '';
        
        if (item.metafield1) {
          metafieldHtml += `<div class="selected-item-metafield">${metafield1Label || 'Info'}: ${item.metafield1}</div>`;
        }
        
        if (item.metafield2) {
          metafieldHtml += `<div class="selected-item-metafield">${metafield2Label || 'Info'}: ${item.metafield2}</div>`;
        }
        
        return `
          <div class="selected-item">
            <div class="selected-item-info">
              <span class="selected-item-title">${item.title}</span>
              ${metafieldHtml}
            </div>
            <button type="button" onclick="window.bundleBuilder_${this.blockId}_removeSelection('${step}', '${item.variantId}')">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2"/>
              </svg>
            </button>
          </div>
        `;
      }).join('');

      // Update dropdown trigger text
      const selector = this.querySelector(`[data-step="${step}"]`);
      const trigger = selector.querySelector('.selected-text');
      if (selections.length > 0) {
        trigger.textContent = selections.length === 1 
          ? selections[0].title 
          : `${selections.length} items selected`;
      } else {
        trigger.textContent = selector.querySelector('.bundle-selector-trigger').dataset.placeholder || 'Choose an option';
      }
    }

    removeSelection(step, variantId) {
      const selections = this.selectedProducts.get(step) || [];
      const updatedSelections = selections.filter(item => item.variantId !== variantId);
      
      this.updateStepSelection(step, updatedSelections);
      
      // Uncheck the corresponding input
      const input = this.querySelector(`input[value="${variantId}"]`);
      if (input) input.checked = false;
      
      this.updateSelectedItemsDisplay(step);
      this.updateSummary();
      this.validateBundle();
    }

    updateSummary() {
      const summaryItems = this.summary.querySelector('.summary-items');
      const summaryPricing = this.summary.querySelector('.summary-pricing');
      
      let allItems = [];
      this.selectedProducts.forEach(items => {
        allItems = allItems.concat(items);
      });

      if (allItems.length === 0) {
        summaryItems.innerHTML = '<div class="summary-placeholder"><p>Select items to build your bundle</p></div>';
        summaryPricing.hidden = true;
        return;
      }

      // Get metafield labels
      const blockElement = this.closest('[data-block-id]');
      const metafield1Label = blockElement?.dataset.metafield1Label || this.dataset.metafield1Label;
      const metafield2Label = blockElement?.dataset.metafield2Label || this.dataset.metafield2Label;

      // Display selected items with metafields
      summaryItems.innerHTML = allItems.map(item => {
        let metafieldHtml = '';
        
        if (item.metafield1) {
          metafieldHtml += `<div class="summary-item-metafield">${metafield1Label || 'Info'}: ${item.metafield1}</div>`;
        }
        
        if (item.metafield2) {
          metafieldHtml += `<div class="summary-item-metafield">${metafield2Label || 'Info'}: ${item.metafield2}</div>`;
        }
        
        return `
          <div class="summary-item">
            ${item.image ? `<img src="${item.image}" alt="${item.title}" loading="lazy">` : ''}
            <div class="summary-item-details">
              <span class="summary-item-title">${item.title}</span>
              ${metafieldHtml}
              <span class="summary-item-price">${this.formatMoney(item.price)}</span>
            </div>
          </div>
        `;
      }).join('');

      // Calculate pricing
      const subtotal = allItems.reduce((sum, item) => sum + item.price, 0);
      const discount = this.bundleDiscount > 0 ? subtotal * (this.bundleDiscount / 100) : 0;
      const total = subtotal - discount;

      // Update pricing display
      this.summary.querySelector('[data-subtotal]').textContent = this.formatMoney(subtotal);
      
      if (this.bundleDiscount > 0) {
        this.summary.querySelector('[data-discount]').textContent = `-${this.formatMoney(discount)}`;
      }
      
      this.summary.querySelector('[data-total]').textContent = this.formatMoney(total);
      summaryPricing.hidden = false;
    }

    validateBundle() {
      const requiredSteps = Array.from(this.querySelectorAll('[data-required="true"]'));
      const allRequiredFilled = requiredSteps.every(step => {
        const stepNumber = step.dataset.step;
        return this.selectedProducts.has(stepNumber) && this.selectedProducts.get(stepNumber).length > 0;
      });

      this.addToCartBtn.disabled = !allRequiredFilled;
      
      if (!allRequiredFilled) {
        this.addToCartBtn.title = 'Please complete all required steps';
      } else {
        this.addToCartBtn.title = '';
      }
    }

    async addBundleToCart() {
      this.hideError();
      this.setLoading(true);

      try {
        const items = [];
        this.selectedProducts.forEach(selections => {
          selections.forEach(item => {
            items.push({
              id: item.variantId,
              quantity: 1
            });
          });
        });

        if (items.length === 0) {
          throw new Error('No items selected');
        }

        // Add bundle discount as a property
        const properties = this.bundleDiscount > 0 ? {
          '_bundle_discount': `${this.bundleDiscount}%`,
          '_bundle_id': Date.now().toString(),
          '_bundle_block': this.blockId
        } : {};

        // Debug logging
        console.log('Adding bundle items to cart:', items);

        // Add items to cart
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: items.map(item => ({
              ...item,
              properties
            }))
          })
        });

        console.log('Response status:', response.status);
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.description || 'Failed to add bundle to cart');
        }

        const responseData = await response.json();
        console.log('Bundle added successfully:', responseData);

        // Force cart refresh
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        console.log('Updated cart:', cart);

        // Update cart count
        this.updateCartCount(cart.item_count);

        // Trigger cart update events
        this.triggerCartUpdate(cart);

        // Open cart drawer or redirect
        await this.openCart(cart);

        // Reset selections
        this.resetBuilder();

      } catch (error) {
        console.error('Error adding bundle to cart:', error);
        this.showError(error.message);
      } finally {
        this.setLoading(false);
      }
    }

    updateCartCount(count) {
      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(el => {
        if (count === 0) {
          el.classList.add('hidden');
          el.textContent = '';
        } else if (count > 99) {
          el.classList.remove('hidden');
          el.classList.add('large-items');
          el.textContent = '';
        } else {
          el.classList.remove('hidden', 'large-items');
          el.textContent = count;
        }
      });
    }

    triggerCartUpdate(cart) {
      // Trigger multiple cart update events for theme compatibility
      const events = [
        new CustomEvent('cart:update', { detail: { cart, source: 'bundle-builder' } }),
        new CustomEvent('cart:count:update', { detail: { count: cart.item_count } }),
        new CustomEvent('cart:refresh', { detail: { cart } }),
        new CustomEvent('ajaxCart:update', { detail: { cart } })
      ];

      events.forEach(event => document.dispatchEvent(event));

      // Try theme-specific updates
      if (window.CartDrawer?.refresh) window.CartDrawer.refresh();
      if (window.theme?.cart?.getCart) window.theme.cart.getCart();
      if (window.cart?.getCart) window.cart.getCart();
    }

    async openCart(cart) {
      const cartDrawer = document.querySelector('cart-drawer, [data-cart-drawer], #cart-drawer');
      
      if (cartDrawer) {
        // Update cart drawer content
        if (cartDrawer.renderContents) {
          const sectionsResponse = await fetch('/cart?sections=cart-drawer');
          const sections = await sectionsResponse.json();
          cartDrawer.renderContents({ sections, cart });
        } else {
          // Fallback: manually update content
          const response = await fetch('/cart?section_id=cart-drawer');
          const html = await response.text();
          const parser = new DOMParser();
          const newDoc = parser.parseFromString(html, 'text/html');
          const newContent = newDoc.querySelector('[data-cart-drawer-content], .cart-drawer__content');
          const currentContent = cartDrawer.querySelector('[data-cart-drawer-content], .cart-drawer__content');
          
          if (newContent && currentContent) {
            currentContent.innerHTML = newContent.innerHTML;
          }
        }
        
        // Open cart drawer
        if (cartDrawer.open) {
          cartDrawer.open();
        } else {
          // Try various methods to open cart
          const cartToggle = document.querySelector('[data-cart-toggle], [data-source="cart-drawer"], .js-drawer-open-right');
          if (cartToggle) cartToggle.click();
        }
      } else {
        // No cart drawer, redirect to cart page
        window.location.href = '/cart';
      }
    }

    resetBuilder() {
      this.selectedProducts.clear();
      this.querySelectorAll('input:checked').forEach(input => {
        input.checked = false;
      });
      this.querySelectorAll('[data-selected-items]').forEach(container => {
        container.innerHTML = '';
      });
      this.querySelectorAll('.selected-text').forEach(text => {
        const trigger = text.closest('.bundle-selector-trigger');
        if (trigger) {
          text.textContent = trigger.dataset.placeholder || 'Choose an option';
        }
      });
      this.updateSummary();
      this.validateBundle();
    }

    setLoading(loading) {
      const btnText = this.addToCartBtn.querySelector('[data-atc-text]');
      const loader = this.addToCartBtn.querySelector('[data-atc-loader]');
      
      if (loading) {
        if (btnText) btnText.hidden = true;
        if (loader) loader.classList.remove('hidden');
        this.addToCartBtn.disabled = true;
      } else {
        if (btnText) btnText.hidden = false;
        if (loader) loader.classList.add('hidden');
        this.validateBundle();
      }
    }

    showError(message) {
      if (this.errorContainer) {
        this.errorContainer.textContent = message;
        this.errorContainer.hidden = false;
        
        // Auto-hide error after 5 seconds
        setTimeout(() => this.hideError(), 5000);
      }
    }

    hideError() {
      if (this.errorContainer) {
        this.errorContainer.hidden = true;
      }
    }

    formatMoney(cents) {
      const dollars = cents / 100;
      return this.currency.replace(/\{\{\s*amount\s*\}\}/, dollars.toFixed(2));
    }
  }

  // Only define custom element once
  if (!customElements.get('product-bundle-builder')) {
    customElements.define('product-bundle-builder', ProductBundleBuilder);
  }

  // Handle dynamic section loading
  document.addEventListener('shopify:section:load', (event) => {
    const bundleBuilders = event.target.querySelectorAll('product-bundle-builder:not([data-initialized])');
    bundleBuilders.forEach(builder => {
      builder.connectedCallback();
    });
  });

  // Handle theme editor changes
  document.addEventListener('shopify:block:select', (event) => {
    const bundleBuilder = event.target.querySelector('product-bundle-builder');
    if (bundleBuilder && !bundleBuilder.hasAttribute('data-initialized')) {
      bundleBuilder.connectedCallback();
    }
  });

  console.log('Bundle Builder script loaded and initialized');
}